package com.example.socialmediaapp.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.example.socialmediaapp.data.models.Notification
import com.example.socialmediaapp.data.models.NotificationType
import com.example.socialmediaapp.network.NotificationsRepository
import com.example.socialmediaapp.utils.TimeFormatter

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun NotificationsScreen(
    notificationsRepository: NotificationsRepository? = null,
    onBack: () -> Unit,
    onNotificationClick: (Notification) -> Unit = {},
    onMarkAllRead: () -> Unit = {},
    // Legacy param for compat
    notifications: List<Notification> = emptyList()
) {
    val scope = rememberCoroutineScope()
    var items by remember { mutableStateOf(notifications) }
    var isLoading by remember { mutableStateOf(notificationsRepository != null) }

    LaunchedEffect(notificationsRepository) {
        if (notificationsRepository == null) return@LaunchedEffect
        isLoading = true
        notificationsRepository.getNotifications().onSuccess { items = it }
        isLoading = false
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Notifications") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, "Back")
                    }
                },
                actions = {
                    TextButton(onClick = {
                        scope.launch {
                            notificationsRepository?.markAllRead()
                            items = items.map { it.copy(isRead = true) }
                        }
                        onMarkAllRead()
                    }) { Text("Mark all read") }
                }
            )
        }
    ) { padding ->
        when {
            isLoading -> Box(
                Modifier.fillMaxSize().padding(padding),
                contentAlignment = Alignment.Center
            ) { CircularProgressIndicator() }

            items.isEmpty() -> Box(
                Modifier.fillMaxSize().padding(padding),
                contentAlignment = Alignment.Center
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Icon(Icons.Default.NotificationsNone, null, Modifier.size(64.dp),
                        tint = MaterialTheme.colorScheme.onSurfaceVariant)
                    Spacer(Modifier.height(16.dp))
                    Text("No notifications", style = MaterialTheme.typography.titleMedium)
                }
            }

            else -> LazyColumn(Modifier.fillMaxSize().padding(padding)) {
                items(items, key = { it.id }) { notification ->
                    ListItem(
                        headlineContent = { Text(notification.title) },
                        supportingContent = {
                            Column {
                                Text(notification.content)
                                Spacer(Modifier.height(4.dp))
                                Text(
                                    TimeFormatter.formatTimestamp(notification.timestamp),
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        },
                        leadingContent = {
                            Icon(
                                when (notification.type) {
                                    NotificationType.MESSAGE -> Icons.Default.Chat
                                    NotificationType.MENTION -> Icons.Default.AlternateEmail
                                    NotificationType.LIKE -> Icons.Default.Favorite
                                    NotificationType.FOLLOW -> Icons.Default.PersonAdd
                                    NotificationType.SUBSCRIPTION -> Icons.Default.CardGiftcard
                                },
                                null,
                                tint = if (notification.isRead)
                                    MaterialTheme.colorScheme.onSurfaceVariant
                                else
                                    MaterialTheme.colorScheme.primary
                            )
                        },
                        modifier = Modifier
                            .clickable { onNotificationClick(notification) }
                            .background(
                                if (!notification.isRead)
                                    MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.1f)
                                else Color.Transparent
                            )
                    )
                    HorizontalDivider()
                }
            }
        }
    }
}
